import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import useTranslation from "next-translate/useTranslation";
import Stepper from "./../../components/Stepper";
import AddListingDetail from "../../components/AddListingDetail";
import SelectProperty from "./../../components/SelectProperty";
import UploadImages from "./../../components/UploadImages";
import ListingCardPreview from "./../../components/ListingCardPreview";
import {
  createListing,
  updateData,
  uploadMediaFiles,
  updateListing,
  getListing,
  updateListingMediaData,
} from "./../../../store/actions/Listing";
import { LISTING } from "./../../config/routes";
import { useRouter } from "next/router";
import screenSize from "./../../states/screen";

export default function CreateListingPage() {
  const dispatch = useDispatch();
  const router = useRouter();
  const isDesktop = screenSize();
  const { t } = useTranslation("common");
  const [activeStep, setActiveStep] = useState(0);
  const [propertyData, setPropertyData] = useState({});
  const [addListingFormSubmitted, setAddListingFormSubmitted] = useState(false);
  const isEdit = router.query && router.query.id ? true : false;
  const ListingReducer = useSelector((state) => {
    return state.ListingReducer;
  });
  const listingData = ListingReducer.listingData;

  useEffect(() => {
    if (
      isEdit &&
      (!ListingReducer.listingDetail || !ListingReducer.listingDetail.title)
    ) {
      dispatch(getListing(router.query.id));
    }
  }, [isEdit]);

  useEffect(() => {
    if (ListingReducer) {
      if (isEdit && ListingReducer.listingDetail) {
        const { property, ...rest } = ListingReducer.listingDetail;
        setPropertyData(property);
        if (!listingData || !listingData._id) {
          dispatch(
            updateData({
              listingData: rest,
              createdSucess: false,
              isLoading: false,
            })
          );
        }
      }

      if (ListingReducer.createdSucess) {
        if (getActiveFormName().finalStep) {
          router.push(LISTING);
        } else if (
          getActiveFormName().formName == "AddListingDetail" ||
          getActiveFormName().formName == "uploadImages"
        ) {
          dispatch(
            updateData({
              createdSucess: false,
              isLoading: false,
            })
          );
          setAddListingFormSubmitted(false);
          moveToNextStep();
        }
      }
    }
  }, [ListingReducer]);

  const handleAddProperty = (values) => {
    setPropertyData(values);
  };

  const stepsFormName = [
    {
      formName: "SelectedProperty",
      required: true,
      validate: () => {
        return !!propertyData.name;
      },
      nextButtonText: t("next"),
    },
    {
      formName: "AddListingDetail",
      required: true,
      nextButtonText: t("next"),
    },
    {
      formName: "uploadImages",
      required: false,
      nextButtonText: t("next"),
    },
    {
      formName: "previewListing",
      required: false,
      finalStep: true,
      nextButtonText: t("publish"),
    },
  ];

  const getActiveFormName = () => {
    return stepsFormName[activeStep];
  };

  const nextButton = () => {
    return (
      <Button
        color="primary"
        variant={isDesktop ? "contained" : "text"}
        disabled={ListingReducer.isLoading}
        onClick={() => {
          const stepForm = getActiveFormName();

          if (stepForm.validate) {
            if (stepForm.validate()) {
              moveToNextStep();
            }
          } else if (stepForm.formName == "AddListingDetail") {
            setAddListingFormSubmitted(true);
          } else if (stepForm.formName == "uploadImages") {
            saveMediaAndOtherDetails();
          } else {
            if (stepForm.finalStep) {
              publishListing();
            } else {
              moveToNextStep();
            }
          }
        }}
      >
        {ListingReducer.isLoading ? (
          <CircularProgress />
        ) : (
          stepsFormName[activeStep].nextButtonText
        )}
      </Button>
    );
  };

  const backButton = () => {
    return (
      <Button
        variant={isDesktop ? "contained" : "text"}
        disabled={ListingReducer.isLoading || activeStep === 0}
        onClick={() => {
          setActiveStep(activeStep - 1);
        }}
      >
        {t("back")}
      </Button>
    );
  };

  const updateListingMediaDataAction = (values) => {
    dispatch(updateListingMediaData(values));
  };

  const handleAddListingDetail = (values) => {
    if (values._id) {
      values.id = values._id;
      values._id = undefined;
      dispatch(
        updateListing({
          ...values,
          propertyId: propertyData._id,
        })
      );
    } else {
      dispatch(
        createListing({
          ...values,
          propertyId: propertyData._id,
        })
      );
    }
  };

  const moveToNextStep = () => {
    setActiveStep(activeStep + 1);
  };

  const handlePropertySelection = (value) => {
    setPropertySuggestion(value);
  };

  const uploadMedia = (images) => {
    dispatch(
      uploadMediaFiles({
        listingId: listingData._id,
        images,
      })
    );
  };

  const saveMediaAndOtherDetails = () => {
    dispatch(
      updateListing({
        id: listingData._id,
        media: listingData.media || [],
        youtubeUrls: listingData.youtubeUrls || [],
        virtualTours: listingData.virtualTours || [],
      })
    );
  };

  const publishListing = () => {
    if (listingData.status === "PUBLISH") {
      router.push(LISTING);
      return;
    }
    dispatch(
      updateListing({
        id: listingData._id,
        status: "PUBLISH",
      })
    );
  };

  return (
    <Stepper
      setActiveStep={setActiveStep}
      activeStep={activeStep}
      nextButton={nextButton}
      backButton={backButton}
      steps={[
        {
          label: t("select_property"),
          view: (
            <SelectProperty
              selectedProperty={propertyData}
              onSelect={handleAddProperty}
            />
          ),
        },
        {
          label: t("listing_detail"),
          view: (
            <AddListingDetail
              listingData={listingData}
              submitForm={addListingFormSubmitted}
              handleSubmitError={() => {
                setAddListingFormSubmitted(false);
              }}
              handleSubmit={handleAddListingDetail}
              property={propertyData}
            />
          ),
        },
        {
          label: t("upload_images"),
          view: (
            <UploadImages
              listingId={listingData.listingId}
              uploadedImages={{
                images: listingData.media,
                youtubeUrls: listingData.youtubeUrls || [],
                virtualTours: listingData.virtualTours || [],
              }}
              uploadMedia={uploadMedia}
              updateListingMediaData={updateListingMediaDataAction}
            />
          ),
        },
        {
          label: t("preview"),
          view: (
            <ListingCardPreview
              publishListing={publishListing}
              propertyData={propertyData}
              listingData={listingData}
              media={listingData.media}
              adminMode={true}
            />
          ),
        },
      ]}
      skipSteps={[]}
      failedSteps={[]}
    />
  );
}
